import { RequiredVersionConfig } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import { RefreshDouble } from 'iconoir-react';
import { useCallback, useEffect, useState } from 'react';
import { BUILD_TIMESTAMP } from '../../constants/version';

interface VersionUpdateModalProps {
  config: RequiredVersionConfig;
}

const DISMISS_STORAGE_KEY = 'sf_version_update_dismissed_at';

/**
 * Modal overlay that prompts users to refresh when a new dashboard version is required.
 */
export function VersionUpdateModal({ config }: VersionUpdateModalProps) {
  const [dismissed, setDismissed] = useState(false);

  // Check if we should show based on dismissal state
  useEffect(() => {
    if (!config.dismissible) {
      setDismissed(false);
      return;
    }

    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!dismissedAt) {
      setDismissed(false);
      return;
    }

    const dismissedTimestamp = parseInt(dismissedAt, 10);
    const dismissDuration = (config.dismissDurationMinutes ?? 30) * 60 * 1000;
    const now = Date.now();

    if (now - dismissedTimestamp > dismissDuration) {
      // Dismissal expired
      localStorage.removeItem(DISMISS_STORAGE_KEY);
      setDismissed(false);
    } else {
      setDismissed(true);
    }
  }, [config.dismissible, config.dismissDurationMinutes]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    setDismissed(true);
  }, []);

  const handleRefresh = useCallback(() => {
    // Force hard refresh to bypass cache
    window.location.reload();
  }, []);

  // Check if current build is outdated
  const isOutdated = BUILD_TIMESTAMP < config.minBuildTimestamp;

  // Don't show if not outdated or if dismissed
  if (!isOutdated || dismissed) {
    return null;
  }

  const defaultMessage =
    'A new version of the dashboard is available. Please refresh to get the latest features and improvements.';
  const canDismiss = config.dismissible ?? true;

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-gray-900/70 backdrop-blur-sm">
      <div className="mx-4 flex max-w-md flex-col items-center gap-6 rounded-2xl bg-white p-8 shadow-2xl">
        {/* Icon */}
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <RefreshDouble className="h-8 w-8 text-blue-600" />
        </div>

        {/* Title */}
        <h2 className="text-center text-xl font-semibold text-gray-900">
          Update Available
        </h2>

        {/* Message */}
        <p className="text-center text-gray-600">
          {config.message || defaultMessage}
        </p>

        {/* Buttons */}
        <div className="flex w-full flex-col gap-3">
          <Button onClick={handleRefresh} className="w-full">
            Refresh Now
          </Button>
          {canDismiss && (
            <Button
              variant="ghost"
              onClick={handleDismiss}
              className="w-full text-gray-500"
            >
              Remind Me Later
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
